/**
 * Core data shapes shared between client and worker.
 *
 * These mirror the D1 schema (apps/worker/migrations/0001_initial.sql) and the
 * REST contract (docs/api/openapi.yaml). Naming is camelCase here; D1 uses
 * snake_case columns. Mappers live in the worker.
 */

export type BilingualText = Record<string, string>;

export interface Concept {
  id: string;
  typeId: string;
  /** Set when this concept references a workspace-defined subtype (Specialization). */
  extendsTypeId: string | null;
  name: BilingualText;
  description: BilingualText;
  attributes: Record<string, unknown>;
  tags: string[];
  ownerEmail: string;
  ownerTeamId: string | null;
  parentId: string | null;
  version: number;
  experimentalInBranch: string | null;
  tombstoneInBranch: string | null;
  /**
   * Stereotype-bundles op dit concept (M5+). Elke string is een
   * stereotype-id zoals `c4.Container`, `sharp.Concept`,
   * `c4.Microservice`. Eén concept kan 0..* stereotypes dragen —
   * bv. een Application Component die tegelijk `«Concept»` (Sharp)
   * én `«Container»` met sub-stereotype `«Microservice»` (C4) is
   * krijgt drie stereotype-ids in deze array. De
   * `RegistryRuntime.canApplyStereotype` valideert per stereotype
   * of de combinatie met `typeId` toegestaan is. Default: lege
   * array (geen stereotypes — gewone ArchiMate-element).
   */
  stereotypes: string[];
  /**
   * Alternatieve labels voor view-laag-resolutie (M5-§3). Key is
   * een resolver-id zoals `"sharp"` of `"c4"`; value is de
   * `BilingualText` die in views met `label_resolver=<key>`
   * getoond wordt i.p.v. de standaard `name`. Wanneer de key
   * ontbreekt of leeg is, valt de view-render-laag terug op
   * `name` (= ArchiMate-naam). Eén concept kan meerdere
   * alt-labels dragen — bv. een multi-stereotype Application
   * Component die zowel `sharpLabel: "Klant"` als
   * `c4Label: "Customer Service"` heeft. Default: leeg object.
   */
  altLabels: Record<string, BilingualText>;
  /**
   * Branch-ownership van het record (M6+). NULL voor pre-M6 records
   * (= effectief main). Bij copy-on-write op een feature-branch krijgt
   * de kopie deze gevuld met de feature-branch's id; via origin_id is
   * de bron traceerbaar. UI gebruikt dit voor de groen-bolletje-indicator.
   */
  branchId?: string | null;
  /**
   * Folder-membership (M6.5-W3+). NULL voor concepten die niet in een
   * folder zitten. Folder-concepten leven uitsluitend in de Base-WP en
   * dragen automatisch het stereotype `«folder.ReferenceObject»`. Voor
   * de Foldertree-rendering (W3b) groepeert de client op deze waarde.
   */
  folderId?: string | null;
  /**
   * Server-berekende mutatie-rechten van de huidige workpackage op dit
   * concept (M6.5+). `false` = het concept zit in een folder waar de
   * actieve (non-Base) WP geen write-subscription op heeft, dus PATCH/DELETE
   * geeft 403. `true`/undefined = vrij muteerbaar. Read-only afgeleide;
   * niet persistent, wordt per listing opnieuw bepaald. De client gebruikt
   * dit om read-only items preventief te blokkeren (bv. de Glossary opent de
   * bewerk-popup niet).
   */
  editable?: boolean;
  /**
   * Logische-identiteits-bron (copy-on-write). NULL voor een origineel; bij een
   * branch-kopie het id van het concept waaruit gekopieerd is. De logische
   * identiteit van een concept = `originId ?? id` — gebruikt om "dezelfde"
   * entiteit over branches/plateaus te herkennen (bv. de Roadmap-matrix die per
   * plateau-branch de `«ConceptState».state_name` van hetzelfde element toont).
   */
  originId?: string | null;
  /**
   * Server-berekende laatste-mutatie-tijdstip (`concepts.updated_at`,
   * ISO/SQLite `YYYY-MM-DD HH:MM:SS` in UTC). Read-only afgeleide die de
   * worker via `rowToConcept` meelevert; niet ingezonden bij create/patch.
   * Optioneel omdat client-side geconstrueerde Concepts (optimistische
   * updates, snapshots) 'm niet zetten. Gebruikt door de Type-explorer
   * (M8 §4) voor de "laatst gewijzigd"-kolom.
   */
  updatedAt?: string;
  /** Door de database opgehoogd bij elke schrijfactie (trigger, migratie
   *  0048). Zegt of jouw exemplaar nog klopt: "ik heb 7, er is 9". Ook een
   *  overschrijving met dezelfde waarden telt — zie
   *  `docs/architecture/gelijktijdigheid.md` §4. */
  rowVersion?: number;
}

export interface Relation {
  id: string;
  typeId: string;
  sourceConceptId: string;
  targetConceptId: string;
  label: BilingualText;
  attributes: Record<string, unknown>;
  ownerEmail: string;
  ownerTeamId: string | null;
  version: number;
  experimentalInBranch: string | null;
  tombstoneInBranch: string | null;
  /**
   * Stereotype-bundles op deze relatie (M5+). Zelfde patroon als
   * `Concept.stereotypes` — bv. een Used By relatie kan een
   * `«C4 Connection»`-stereotype dragen met `connectionTechnology`-
   * attribuut. Default: lege array.
   */
  stereotypes: string[];
  /** Alternatieve labels per view-resolver-id (M5-§3). Zelfde patroon als `Concept.altLabels`. Default leeg object. */
  altLabels: Record<string, BilingualText>;
  /** Server-berekende laatste-mutatie-tijdstip (`relations.updated_at`, UTC).
   *  Read-only afgeleide; optioneel (client-side geconstrueerde relaties
   *  zetten 'm niet). Gebruikt door het TBD/NTB-rapport (M8). */
  updatedAt?: string;
  /** Door de database opgehoogd bij elke schrijfactie (trigger, migratie
   *  0048). Zegt of jouw exemplaar nog klopt: "ik heb 7, er is 9". Ook een
   *  overschrijving met dezelfde waarden telt — zie
   *  `docs/architecture/gelijktijdigheid.md` §4. */
  rowVersion?: number;
  /**
   * Branch-ownership van het record (M6+). NULL voor pre-M6 records
   * (= effectief main). Voor branch-eigen kopieën via copy-on-write
   * wijst deze naar de feature-branch's id; via origin_id is de bron
   * traceerbaar. UI gebruikt dit voor de groen-bolletje-indicator.
   */
  branchId?: string | null;
}

export type ViewState = Record<string, ViewElementState>;

/**
 * Gereserveerde sleutel in `Diagram.viewState` waaronder de heat-map-
 * configuratie wordt geserialiseerd voor view-kinds met `layout_mode =
 * "heat-map"` (analytics-pack). Gekozen voor stash-in-viewState zodat
 * geen DB-migratie nodig is — `view_state_json` is al een vrij JSON-
 * blob. Niet-heat-map-views negeren deze key.
 *
 * Helpers in `apps/web/src/editor/canvas/heatMapConfig.ts` lezen/schrijven
 * dit veld; canvas-renderer en config-paneel mogen niet rechtstreeks naar
 * deze string-literal verwijzen — gebruik `readHeatMapConfig` /
 * `writeHeatMapConfig`.
 */
export const HEAT_MAP_CONFIG_KEY = "__heatmap_config";

/**
 * Configuratie voor een heat-map-diagram (analytics-pack, view-kind
 * `analytics.heat-map`). Drie keuzes:
 *
 *  - `targetTypeId` of `targetStereotypeId`: welk (sub)type plot je?
 *    Een gewone ArchiMate-typeId (bv. `archimate.BusinessActor`) OF een
 *    stereotype-id (bv. `risk.Vulnerability`). Concepten worden gefilterd
 *    op base-type-match OF op stereotype-array-membership. Beide leeg =
 *    diagram is nog niet geconfigureerd, renderer toont config-prompt.
 *  - `verticalAttribute`: naam van het enum-attribuut dat verticaal wordt
 *    geplot (de y-as). Moet een enum-attribuut zijn op het gekozen
 *    type/stereotype. Boven = eerste enum-optie (default: hoog), onder
 *    = laatste optie (default: laag).
 *  - `horizontalAttribute`: idem voor de x-as. Links = eerste optie,
 *    rechts = laatste optie.
 */
export interface HeatMapConfig {
  targetTypeId?: string | undefined;
  targetStereotypeId?: string | undefined;
  verticalAttribute?: string | undefined;
  horizontalAttribute?: string | undefined;
}

/**
 * Gereserveerde sleutel in `Diagram.viewState` waaronder de roadmap-matrix-
 * configuratie wordt geserialiseerd voor view-kinds met `layout_mode =
 * "roadmap-matrix"` (roadmap-pack, view-kind `roadmap.roadmap-matrix`).
 * Stash-in-viewState zoals HEAT_MAP_CONFIG_KEY — geen DB-migratie nodig.
 * Helpers in `apps/web/src/editor/canvas/roadmapMatrixConfig.ts` lezen/
 * schrijven dit veld; renderer en config-bar mogen niet rechtstreeks naar
 * de string-literal verwijzen.
 */
export const ROADMAP_MATRIX_CONFIG_KEY = "__roadmap_matrix_config";

/**
 * Per-box tekst/vul-opmaak voor de custom render-paden (Roadmap +
 * Roadmap-matrix), opgeslagen als stash in `Diagram.viewState` onder
 * `DIAGRAM_BOX_STYLES_KEY` → `Record<boxKey, BoxStyle>`. Box-keys:
 *   - matrix element-card: `el:<conceptId>`
 *   - matrix toestand-cel:  `cell:<conceptId>:<plateauBranchId>`
 *   - roadmap element-card:  `roadmap:<laneBranchId>:<conceptId>`
 * Geen DB-migratie — speelt mee in de bestaande `view_state_json`-blob.
 */
export const DIAGRAM_BOX_STYLES_KEY = "__box_styles";

export interface BoxStyle {
  fontFamily?: string | undefined;
  fontSize?: number | undefined;
  fontColor?: string | undefined;
  fillColor?: string | undefined;
  /** Horizontale tekst-uitlijning binnen de box. */
  textAlign?: "left" | "center" | "right" | undefined;
  /** Verticale positie van de tekst in de box. */
  verticalAlign?: "top" | "middle" | "bottom" | undefined;
}

/**
 * Configuratie voor een Roadmap-matrix-diagram (roadmap-tijdreizen.md §6.4).
 * Eén template, bij aanmaken beperkt door twee keuzes; daarna sleep je
 * elementen erop.
 *
 *  - `programId`: concept-id van het Program (= Roadmap) waaraan de matrix
 *    vastzit. Bepaalt de plateau-keten = de kolommen. Leeg = nog niet
 *    geconfigureerd → renderer toont config-prompt.
 *  - `conceptFilter`: welk concept-type/domein op de matrix gesleept mag
 *    worden. Een base-type-id (bv. `archimate.BusinessObject`), een
 *    stereotype-id (bv. `sharp.Concept`), of een pack/domein-marker
 *    (bv. `bmc`). De renderer beslist eligibility hierop. Leeg = nog niet
 *    geconfigureerd.
 *  - `rowConceptIds`: concept-ids die de modelleur op de matrix heeft
 *    gesleept (de zaken-van-belang). Volgorde = rij-volgorde. De cel per
 *    plateau wordt berekend uit de branch-overlay; niet hier opgeslagen.
 */
export interface RoadmapMatrixConfig {
  programId?: string | undefined;
  conceptFilter?: string | undefined;
  rowConceptIds?: string[] | undefined;
  /**
   * Per-cel `«ConceptState»`-stateName: `rowStates[conceptId][plateauBranchId]`
   * = de toelichting/betekenis van dat element op dat plateau. **Meertalig**
   * (`BilingualText`) — auto-vertaald op blur, net als naam/beschrijving. Legacy
   * waarden waren platte strings; `readRoadmapMatrixConfig` normaliseert die naar
   * `{ nl, en }`. Pragmatische opslag (chunk C2); de zuivere variant zet 'm als
   * `«roadmap.ConceptState».state_name` op de branch-kopie van het element op die
   * plateau-branch (zie roadmap-tijdreizen.md §6.4) — volgt later.
   */
  rowStates?: Record<string, Record<string, BilingualText>> | undefined;
}

export interface ViewElementState {
  /**
   * Teller per voorkomen. Een voorkomen van een object op een diagram is een
   * uniek gegeven: twee mensen mogen 'm niet tegelijk wijzigen, terwijl twee
   * mensen op verschillende voorkomens elkaar juist niet in de weg mogen
   * zitten. Wie schrijft stuurt mee welke stand 'ie in handen had; klopt die
   * niet, dan wordt díe sleutel geweigerd.
   *
   * Bijgehouden door `viewStateMerge.ts` aan de serverkant — niet door een
   * databasetrigger, want een entry zit in een JSON-blob en niet in een kolom.
   * Zie `docs/architecture/gelijktijdigheid.md`.
   */
  version?: number;
  /**
   * Discriminator: welke soort entiteit deze viewState-entry refereert.
   *
   * - `"object"` — entry representeert een (visuele instance van een)
   *   concept. Vereist een `position` om gerenderd te worden; gebruikt
   *   de object-specifieke velden hieronder (width, height, containerId,
   *   nodeStyle, zOrder, collapsedAsContainer, tableOrder, slotId,
   *   orderInSlot).
   * - `"relation"` — entry representeert een (visuele instance van een)
   *   relation. Heeft geen `position` (de positie volgt uit de endpoints);
   *   gebruikt de relation-specifieke velden hieronder (sourceHandle,
   *   targetHandle, sourceAngle, targetAngle, sourceInstanceId,
   *   targetInstanceId, waypoints, routing, labelOffset, lineColor,
   *   lineStyle, labelBgColor, labelTextColor, labelMaxWidth,
   *   sourceLabelInset/Width/Offset, targetLabelInset/Width/Offset).
   * - `"supernode"` — synthetische view-only super-node (Fase 2 diagram-
   *   vereenvoudiging: layer-collapse / promote-to-abstraction). Bestaat
   *   ALLEEN in de viewState — er is géén onderliggend Concept; `ref` is
   *   de entry-key zelf (sentinel-stijl). Gebruikt `position`/`width`/
   *   `height` + de `supernode`-databag. Leden wijzen ernaar via hun
   *   `containerId` (+ `hidden: true`) zodat de bestaande edge-re-anchor
   *   hun relaties op de super-node projecteert. Emitters, matrix, store
   *   en Explorer negeren dit type (geen concept). `normalizeViewState`
   *   laat 't ongemoeid (nooit orphan).
   *
   * Fase-2 (2026-05-26): required veld. Schrijf-paden zetten 'm
   * expliciet bij elke nieuwe entry. Bestaande data zonder type wordt
   * door `normalizeViewState` aangevuld op basis van de entry-key
   * (concept → "object", relation → "relation").
   */
  type: "object" | "relation" | "supernode";
  /**
   * Databag voor `type === "supernode"`-entries. `memberConceptIds` zijn de
   * concept-ids die in deze super-node zijn samengevouwen (hun instances
   * staan hidden + containerId → deze entry). `layer` stuurt de renderkleur
   * (ArchiMate-laag-token); `label` is een optionele custom naam (bv. bij
   * promote-to-abstraction), anders rendert de client het laag-label.
   */
  supernode?:
    | {
        layer?: string;
        label?: BilingualText;
        memberConceptIds: string[];
        /**
         * Optioneel pseudo-type voor render (promote-to-abstraction). Bv.
         * `archimate.ApplicationCollaboration` → het blok krijgt de type-kleur
         * + type-icoon i.p.v. de laag-default. Er is GEEN echt concept van dit
         * type; puur visueel. Leeg = laag-gestileerd blok (layer-collapse).
         */
        typeId?: string;
      }
    | undefined;
  /**
   * UUID van de onderliggende entiteit: een concept-id wanneer
   * `type === "object"`, een relation-id wanneer `type === "relation"`.
   *
   * Vervangt het oudere `conceptRef`-veld (gedropt 2026-05-26 in
   * Fase-2-cleanup). Eén veld voor beide soorten, met `type` als
   * discriminator.
   *
   * Bij canonieke placement (de eerste verschijning van een concept of
   * relation op een diagram) is `ref === entry-key`. Bij multi-instance
   * placements is de entry-key een fresh UUID en wijst `ref` terug naar
   * de domain-id.
   *
   * Fase-2 (2026-05-26): required veld. Schrijf-paden zetten 'm
   * expliciet. Voor pre-Fase-1 data zonder ref doet `normalizeViewState`
   * de aanvulling tijdens hydrate (= entry-key, of legacy conceptRef
   * via cast voor admin-migration).
   */
  ref: string;
  /**
   * Generieke per-instance attribuut-bag. Wordt gebruikt wanneer de
   * per-instance opslag-vlag (`storeOnInstance`, hieronder) op deze
   * instance `true` is: dan leven álle attribuut-waarden van de
   * onderliggende concept/relation in deze bag i.p.v. op de entity.
   * Bijvoorbeeld een `risk.CountermeasureDeployment`-stereotype op een
   * relation-instance kan zo per asset-deployment een eigen `owner`,
   * `effectiveness`, `last_tested_on` etc. dragen.
   *
   * Bij `storeOnInstance` afwezig/false leven de waarden op de entity
   * zelf (`Concept.attributes` / `Relation.attributes`) en is deze bag
   * niet relevant.
   *
   * Geïntroduceerd in Stap 2 van de multi-instance-refactor (2026-05-26);
   * sinds 2026-05-28 gestuurd door de `storeOnInstance`-vlag i.p.v. het
   * (verwijderde) `scope: "instance"`-stereotype-mechanisme.
   */
  stereotypeAttributes?: Record<string, unknown> | undefined;
  /**
   * Per-instance opslag-vlag (per-instance-attribuut-flag, 2026-05-28 —
   * zie `docs/architecture/per-instance-attribute-flag.md`). Vervangt het
   * `scope: "instance"`-stereotype-mechanisme.
   *
   * - `true`  → álle attribuut-waarden van dit object/deze relatie worden
   *   op deze instance (in `stereotypeAttributes`, de generieke instance-
   *   attribuut-bag) opgeslagen i.p.v. op de entity. Lezen: instance-bag
   *   eerst, val terug op de entity voor ontbrekende velden.
   * - `false`/afwezig → waarden leven op de entity
   *   (`Concept.attributes` / `Relation.attributes`). Afwezig = Nee; we
   *   persisteren de vlag alleen wanneer 'ie `true` is.
   *
   * Toggle-semantiek (door AttributePanel): Nee→Ja kopieert de huidige
   * entity-waarden naar de instance-bag als startpunt; Ja→Nee wist de
   * instance-bag zodat de entity weer de bron is.
   */
  storeOnInstance?: boolean | undefined;
  /** Position on the canvas. Undefined for elements not yet laid out. */
  position?: { x: number; y: number };
  /**
   * Optional explicit dimensions. When absent, the node sizes itself
   * around its content (label + type label). When set (e.g. by the user
   * dragging a resize handle), the canvas applies these as inline styles
   * so the box stays at that exact size — useful for container vakjes
   * that hold nested children.
   */
  width?: number;
  height?: number;
  /**
   * User-set z-stack positie op het canvas. Hoger = meer naar voren,
   * lager = meer naar achteren. Default = undefined → Canvas.tsx kiest
   * een sensible default (drawing-primitives -10, normale concepten 0).
   * Gemanipuleerd door de "naar voren / naar achteren / helemaal
   * onder / helemaal boven"-knoppen in CanvasToolbar.
   *
   * `collapsedAsContainer` (collapse-feature) blijft voorrang hebben
   * en forceert zIndex 1001 ongeacht zOrder — anders zou een collapsed
   * container z'n descendants niet meer kunnen verbergen.
   */
  zOrder?: number;
  /**
   * Volgorde-anker voor de "Tabel (nieuw)"-rij in de Onderwater-pane.
   * Wanneer aanwezig sorteert GridPane rijen op `tableOrder ?? naturalIdx`
   * binnen de concept- resp. relation-groep. Wordt gezet bij "Regel
   * invoegen" via popup of context-menu (insert-na-cursor): nieuwe
   * waarde = midpoint van anker-rij en volgende rij. Bestaande items
   * krijgen pas een waarde wanneer ze zelf het anker of een buur worden
   * van een insert; voor de oude tabel-volgorde verandert dus niets
   * tenzij de gebruiker bewust invoegt. Geen schema-impact — viewState
   * is een JSON-blob in D1.
   */
  tableOrder?: number;
  /**
   * Per-instance items voor `drawing.ListBox`-objecten. Elke entry
   * verwijst naar een concept (kind="concept") of diagram (kind="diagram")
   * dat in deze specifieke list-box instance verschijnt. Sortering
   * gebeurt at-render-time op de actieve UI-taal (lookup via store).
   *
   * Per-instance storage — zelfde list-box-type op een ander diagram
   * heeft z'n eigen items-lijst. Concept- en diagram-ids overleven
   * delete-en-recreate niet (geen referential-integrity-check); een
   * gewist concept of diagram blijft als "dangling" item zichtbaar
   * totdat de gebruiker 't via rechtsklik verwijdert.
   *
   * Toegevoegd 2026-05-27 voor drawing.ListBox-type. Genegeerd voor
   * andere viewState-entries.
   */
  listboxItems?: Array<{ id: string; kind: "concept" | "diagram" }>;
  /** Optional per-diagram styling overrides. */
  style?: Record<string, unknown>;
  /** Indicates whether this element is hidden in this specific view. */
  hidden?: boolean;
  /**
   * Origin-marker voor `hidden`. Wanneer `true`, is deze hide veroorzaakt
   * door een **auto-nest-actie** (bv. M2 `nestOnDiagram` die een binnen-
   * container relatie automatisch verbergt om visueel-clutter te
   * voorkomen). User-hides via Delete-on-edge zetten alleen `hidden:
   * true` zonder deze marker.
   *
   * Wordt automatisch weggehaald wanneer `hidden` op false/undefined
   * gezet wordt; bestaat zelfstandig nooit zonder `hidden`.
   */
  hiddenByAutoNest?: boolean;
  /**
   * **Container-collapse state** (alleen op concept-instances die als
   * container fungeren — d.w.z. minstens één descendant via `containerId`
   * naar deze instance verwijst). Wanneer `true`: de container schildert
   * **bovenop** zijn descendants i.p.v. eronder, zodat de inner content
   * + binnenste edge-segmenten visueel verborgen worden door de container's
   * opake achtergrond. De edges blijven semantisch verbonden met de
   * descendants (geen re-anchor) — de browser regelt de visuele clipping
   * via z-index.
   *
   * Wordt geset/gewist door `toggleCollapseContainer`. Geen impact op
   * descendants' eigen `hidden`-flag (die blijft `undefined`/`false`).
   */
  collapsedAsContainer?: boolean;
  /**
   * Visual containment on this diagram: the view-state entry key (=
   * canvas node id) of the parent vakje. When set, the renderer nests
   * this node inside that parent and the `position` is interpreted as
   * RELATIVE to the parent's top-left corner. Cleared when the user
   * drags the child back out.
   *
   * This is purely a per-diagram visual concern; the workspace-level
   * `Concept.parentId` (structural composition) is independent and
   * untouched by nesting.
   */
  containerId?: string | undefined;
  /**
   * For relation entries: the handle id (left/right/top/bottom) the source
   * endpoint is attached to. Concepts ignore this field.
   */
  sourceHandle?: string | undefined;
  /** For relation entries: the handle id of the target endpoint. */
  targetHandle?: string | undefined;
  /**
   * Angle (degrees, 0 = right, 90 = down, 180 = left, 270 = up) of the
   * source attachment point computed via `point_from_angle`. When set, the
   * renderer uses it instead of the handle midpoint so the line can attach
   * at any arbitrary position on the node's border.
   */
  sourceAngle?: number | undefined;
  /** Same as sourceAngle for the target endpoint. */
  targetAngle?: number | undefined;
  /**
   * For relation entries on diagrams where the source concept appears
   * multiple times: the instance-id (a key in the same `viewState`
   * record) the source endpoint should attach to. Falls back to the
   * canonical placement when absent or when the named instance has no
   * position.
   */
  sourceInstanceId?: string | undefined;
  /** Same as sourceInstanceId for the target endpoint. */
  targetInstanceId?: string | undefined;
  /**
   * For relation entries: ordered list of intermediate bend-points (in
   * absolute flow coordinates). Empty / absent means the edge follows
   * the default smoothstep routing between the two endpoints. Set by
   * the user dragging on a line — see RoutedEdge.
   */
  waypoints?: Array<{ x: number; y: number }> | undefined;
  /**
   * Voor relatie-entries op het **banen-canvas**: een knik in de stroom,
   * bewust NIET als absolute punten.
   *
   * Dat canvas kent geen coördinaten — de plaatsing wordt bij elke render
   * afgeleid uit de banen en de stroomdiepte. Een vast punt zou daardoor
   * scheef komen te staan zodra er een stap bijkomt of iemand naar een andere
   * baan verhuist. Vandaar twee getallen ten opzichte van de afgeleide route:
   * `langs` is de plek op de lijn (0 = bij de bron, 1 = bij het doel) en
   * `opzij` de loodrechte afwijking in punten (negatief = omhoog). De knik
   * schuift zo mee met wat eronder verandert.
   */
  swimlaneKnik?: { langs: number; opzij: number } | undefined;
  /**
   * VERVALLEN (september 2026). Een handmatig gekozen kolom op het
   * banen-canvas, uit de tijd dat de plaatsing daar werd afgeleid in plaats
   * van opgeslagen. Sinds dat canvas z'n stappen gewoon op `position` zet —
   * zoals elk ander canvas — heeft niemand dit veld meer nodig. Het staat er
   * nog zodat bestaande diagrammen niet struikelen over onbekende velden;
   * niets leest 'm nog.
   */
  swimlaneKolom?: number | undefined;
  /**
   * Voor baan-bewoners op het banen-canvas: de plek van hun baan van boven
   * naar beneden.
   *
   * Zonder dit veld staan de banen op naam gesorteerd, en dat is zelden de
   * volgorde die het verhaal vertelt: Sharp begint bovenaan bij wie het werk
   * aanvraagt en daalt af naar wie het afhandelt (Fig 8.1, p204). Ongezet =
   * de oude alfabetische volgorde, achter de banen die wél een plek hebben.
   */
  swimlaneRij?: number | undefined;
  /**
   * Voor baan-bewoners: de hoogte van hun baan, in punten.
   *
   * De standaardhoogte past één rij kaarten. Wie twee stappen onder elkaar
   * wil zetten — parallel werk binnen dezelfde uitvoerder — maakt de baan
   * hoger door aan de onderrand te slepen. Ongezet = de standaardhoogte.
   */
  swimlaneBaanHoogte?: number | undefined;
  /**
   * In WELKE baan dit voorkomen ligt — het concept-id van de bewoner van die
   * baan.
   *
   * Zonder dit veld is de baan een AFLEIDING uit de y-positie, en dat houdt
   * geen stand: maak je een baan lager, dan schuift alles eronder omhoog
   * terwijl de kaarten blijven liggen, en ineens staat een samenvoegpunt in
   * de baan van de buurman of onder het raster. Voor een STAP is er nog een
   * tweede bron — de toewijzing aan z'n uitvoerder — maar een resultaat, een
   * gebeurtenis, een werkobject en een vertakking hebben die niet: die hingen
   * aan niets dan hun y (gemeld 2026-09-19: "zaken die in een baan komen te
   * staan zijn onderwater niet aan de baan gekoppeld").
   *
   * Staat 'ie gezet, dan bepaalt hij de baan en wordt de y binnen die baan
   * gehouden; de kaart schuift dus mee met z'n baan. Ongezet = de oude
   * afleiding uit de y, zodat bestaande diagrammen blijven staan zoals ze
   * stonden tot je ze aanraakt.
   */
  swimlaneBaanId?: string | undefined;
  /**
   * For relation entries: per-edge routing strategy. When unset the edge
   * uses the workspace default ("smoothstep"). "manual" means the route
   * is fully described by `waypoints`; it's set automatically the first
   * time the user bends a line. Other values pick a built-in router.
   */
  routing?: "smoothstep" | "straight" | "bezier" | "step" | "manual" | undefined;
  /**
   * For relation entries: pixel offset of the label relative to the
   * route's natural mid-point. The user can drag the label off the line
   * (e.g. above or below) and we persist the displacement here so it
   * stays put across reloads. Absent / {0,0} means default placement.
   */
  labelOffset?: { x: number; y: number } | undefined;
  /**
   * For concept-instance entries: per-node visual overrides set via the
   * canvas toolbar (bold, italic, font size, fill colour, …). Absent means
   * the node renders with the layer-default colour and normal typography.
   */
  nodeStyle?: NodeStyle | undefined;
  /**
   * For relation entries: stroke colour for the line. When absent, the
   * line uses the relation-type's default colour. Hex string ("#RRGGBB").
   */
  lineColor?: string | undefined;
  /**
   * For relation entries: solid background colour behind the edge-label.
   * When absent, the label-background is transparent (text only, with a
   * subtle white text-shadow for legibility). Useful when an edge crosses
   * over a coloured node and the label needs an opaque box for readability.
   * Hex string ("#RRGGBB") — set via the toolbar swatch.
   */
  labelBgColor?: string | undefined;
  /**
   * For relation entries: explicit text-colour of the edge-label. When
   * absent, the label uses its relation-type default (slate-600 voor
   * gewone relaties, donker bij Influence). Hex string — set via toolbar.
   */
  labelTextColor?: string | undefined;
  /**
   * For relation entries: de opmaak van het edge-label. Vlak gehouden, net als
   * `labelTextColor` en `labelBgColor` hiernaast — bij een relatie hangt de
   * opmaak aan het label en niet aan een vorm, dus er is geen `nodeStyle`-
   * achtig blok om 'm in te hangen.
   *
   * Ontbreekt een veld, dan geldt de standaard van het relatietype: 11px,
   * niet vet, niet cursief.
   */
  labelFontSize?: number | undefined;
  labelBold?: boolean | undefined;
  labelItalic?: boolean | undefined;
  labelUnderline?: boolean | undefined;
  labelFontFamily?: string | undefined;
  /**
   * For relation entries: explicit max-width (in pixels) for the
   * edge-label container. Set via Cmd/Ctrl-drag on the label so the
   * user can squeeze a long single-line label into a multi-line wrap.
   * When absent, the label uses its intrinsic content width.
   */
  labelMaxWidth?: number | undefined;
  /**
   * For relation entries: text-align van de tekst binnen het edge-
   * label-blok. Default = "center" (huidige hardcoded waarde).
   * Sluit aan op `NodeStyle.labelTextAlign` voor concept-labels.
   */
  labelTextAlign?: "left" | "center" | "right" | undefined;
  /** Word-wrap-strategie voor relatie-labels (zie NodeStyle.labelWrap). */
  labelWrap?: "hyphenate" | "break-anywhere" | undefined;
  /**
   * BMC Pakket B (M5.5/M6.0): canvas-grid-positionering. Wanneer de
   * actieve view-kind `layout_mode = "canvas"` heeft (bv. `bmc.canvas`
   * met `bmc.canvas.v1`-template), gebruikt de grid-renderer dit veld
   * om te bepalen in welk template-slot de node hoort. `slotId` is
   * mutually exclusive met `position`/`width`/`height` — een
   * canvas-node krijgt z'n positie van de slot, niet van x/y.
   *
   * In Pakket A (huidig) wordt dit veld nog niet gelezen; renderer-
   * implementatie volgt in Pakket B. Vroeg toegevoegd zodat ViewState-
   * data die in B wordt geschreven niet als onbekend veld wordt
   * weggegooid bij hydrate-paths die strict-mode draaien.
   */
  slotId?: string | undefined;
  /**
   * BMC Pakket B: stack-volgorde binnen één slot wanneer meerdere
   * concepten dezelfde `slotId` delen. Default 0 = bovenaan; hoger =
   * verder naar beneden in de slot-stack. User-drag-up/down past dit
   * aan (zelfde patroon als sort_order op sharp.Attribute /
   * sharp.Identifier in compartments).
   */
  orderInSlot?: number | undefined;
  /**
   * For relation entries: stroke pattern. Defaults to the relation-
   * type's intrinsic style (typically "solid"). Set via the toolbar's
   * "Lijnstijl" dropdown — `"dashed"` and `"dotted"` apply
   * `strokeDasharray` while `"solid"` keeps a continuous line. Per
   * design: ArchiMate-relation-types may have semantic dash patterns
   * (e.g. Realization is dashed-by-spec); user-override here always
   * wins for visual emphasis. Use sparingly.
   */
  lineStyle?: "solid" | "dashed" | "dotted" | "none" | undefined;
  /**
   * For Sharp ConceptAssociation/IdentifyingAssociation relation entries:
   * pixel offset langs de lijn waarop de forward_name (source-side) en
   * reverse_name (target-side) labels worden geplaatst. Default 14 (= de
   * pad-waarde in RoutedEdge). Hoger = label dichter naar het midden
   * geschoven; lager / negatief = dichter bij de node-rand. User
   * versleept de labels langs de lijn om dit aan te passen — handig
   * wanneer een korte lijn de twee namen anders zou laten overlappen,
   * of wanneer een label tegen de cardinaliteit-marker botst.
   */
  sourceLabelInset?: number | undefined;
  targetLabelInset?: number | undefined;
  /**
   * Max-breedte (px) van forward_name / reverse_name labels op
   * Sharp ConceptAssociation-relaties. Ingesteld via Cmd/Ctrl-drag
   * op het label. Wanneer overschreden wraps de tekst over meerdere
   * regels via <tspan>. `undefined` = intrinsieke breedte (één regel).
   */
  sourceLabelWidth?: number | undefined;
  targetLabelWidth?: number | undefined;
  /**
   * Vrije 2D-offset (px in flow-coords) ten opzichte van de default-
   * positie nabij source / target uiteinde. Net als labelOffset op
   * het standaard relatie-label kan de gebruiker forward_name /
   * reverse_name overal naartoe slepen — niet meer vastgepind aan
   * de lijn. Default {0,0} = bij het uiteinde van de lijn.
   */
  sourceLabelOffset?: { x: number; y: number } | undefined;
  targetLabelOffset?: { x: number; y: number } | undefined;
}

export interface NodeStyle {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  fontSize?: number;
  fontFamily?: string;
  fontColor?: string;
  fillColor?: string;
  /**
   * Per-node CSS border-radius override. Set via de Size-groep:
   *   - "50%" → ellips (volgt box-aspect; vierkant = cirkel, rechthoek = ellips)
   *   - undefined → standaard (volgt shape-default uit archimateShapes / type)
   *   - andere CSS-waarden (bv. "12px") zijn mogelijk maar de huidige UI
   *     exposeert alleen ellips-toggle.
   */
  borderRadiusOverride?: string;
  /**
   * Per-node border (stroke) colour. When absent, the node uses the
   * layer-derived default border. Hex string ("#RRGGBB") — set via the
   * toolbar swatch. Mirrors the per-edge `lineColor` mechanic.
   */
  strokeColor?: string;
  /**
   * Per-node border pattern. Defaults to "solid" via CSS. Set via
   * dezelfde Lijnstijl-dropdown als de relatie-strokeDasharray, zodat
   * een gestreepte rand ook voor concepten / drawing-rechthoeken
   * werkt. `"solid"` overschrijft eventuele aspect-defaults.
   */
  borderStyle?: "solid" | "dashed" | "dotted" | "none";
  /**
   * Where the node's label sits relative to the visible shape on the
   * horizontal axis. Defaults to "center". Combined with `labelAlignY`
   * this gives a 3×3 anchor grid (BiZZdesign-style); `labelOutside`
   * flips inside-the-box vs outside-the-box for non-center anchors.
   */
  labelAlignX?: "left" | "center" | "right";
  /**
   * CSS text-align voor de tekst BINNEN het label-blok. Anders dan
   * `labelAlignX` (= waar het label-blok zit) bepaalt dit hoe de
   * tekst-regels in dat blok worden uitgelijnd: links / midden /
   * rechts. Default = volgt `labelAlignX` voor backward-compat
   * (oude entries zonder dit veld blijven identiek renderen).
   */
  labelTextAlign?: "left" | "center" | "right";
  /**
   * Word-wrap-strategie voor lange labels die niet binnen de box passen:
   * - "hyphenate" → CSS `hyphens: auto` + `lang`-attr; browser splitst op
   *   woord-grenzen en plaatst een afbreekstreepje (Klant-\\nonboarding).
   *   Werkt voor NL/EN; goede kwaliteit voor standaard-woorden.
   * - "break-anywhere" → CSS `overflow-wrap: anywhere`; harde mid-word
   *   break zonder streepje. Taal-agnostisch, valt terug op character-
   *   level breaks voor woorden zonder bekend hyphenation-patroon.
   * - undefined (default) → geen wrap; lange woorden overflow'en of
   *   worden geknipt zoals nu.
   */
  labelWrap?: "hyphenate" | "break-anywhere";
  /**
   * De leesrichting van het label. `"vertical"` draait de tekst een kwartslag
   * en laat 'm van onder naar boven lopen — de vorm die Sharp voor baannamen
   * gebruikt (Fig 8.1, p204) en die ook op een gewoon canvas van pas komt bij
   * een smal, hoog vakje. Ongezet = horizontaal.
   */
  labelOrientation?: "horizontal" | "vertical";
  /** Vertical anchor — see `labelAlignX`. Defaults to "middle". */
  labelAlignY?: "top" | "middle" | "bottom";
  /**
   * When true, the label is rendered OUTSIDE the box, in the direction
   * indicated by the X/Y anchors (e.g. top-right + outside puts the
   * label diagonally outside the top-right corner). Ignored when both
   * anchors are at the centre. Defaults to false.
   */
  labelOutside?: boolean;
  /**
   * When true, the small corner-icon (top-right element-type badge) is
   * hidden. Useful wanneer 't symbool zelf de typering al duidelijk
   * maakt (bv. ArchiMate-symbool-modus) of voor minimalistische
   * diagrammen. Toggle via Toolbar → Object → "Verberg icoon".
   */
  hideIcon?: boolean;
}

export interface Diagram {
  id: string;
  /**
   * Bilingual diagram name. UI components resolve to a display string via
   * `resolveBilingual(d.name, lang)`. Stored as JSON in D1's
   * `diagrams.name_json` column.
   */
  name: BilingualText;
  /**
   * Optional bilingual description. When non-empty, the PlantUML
   * emitter includes it as a `title` statement at the top of the
   * generated text. Shown in the editor's diagram-properties dialog.
   */
  description?: BilingualText;
  notationPack: string;
  viewKind: string;
  viewState: ViewState;
  /** Zie `Concept.rowVersion`. Op een diagram telt ook een verschoven vakje
   *  mee: viewState zit in dezelfde rij. */
  rowVersion?: number;
  /**
   * Diagram-niveau attributen, parallel aan `Concept.attributes`.
   * Schema's worden gedeclareerd op de view-kind (zie `ViewKind.attributes`).
   * Eerste use-case: heat-map-diagrammen (analytics-pack) bewaren hier
   * `target_type`, `vertical_attribute`, `horizontal_attribute` plus de
   * vier kleur-velden. Toekomstige view-kinds met eigen configuratie
   * (bv. matrix-view, capability-map) gebruiken hetzelfde mechanisme.
   *
   * Persisted als `attributes_json` op `diagrams` (migration 0031).
   */
  attributes: Record<string, unknown>;
  /**
   * Stereotypes-bag, analoog aan `Concept.stereotypes` / `Relation.stereotypes`.
   * Eerste gebruik: `meta.Retired` — zie docs/architecture/05-backlog.md
   * "Onderhoud-pagina: opruimen retired objecten". Persisted als
   * `stereotypes_json` op `diagrams` (migration 0037).
   */
  stereotypes: string[];
}
